import { Events, UnityBridge } from "@Context/common/general/context";
import useUserPreferences from "@hooks/common/user/use-user-preferences";
import { useGlobalContext } from "@hooks/index";
import i18next from "@i18next";
import Preferences from "@Shared/enums/Preferences";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/es";
import { createContext, Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  clearTemporaryLocale,
  createCountryNames,
  fallbackLocale,
  getInitialLocale,
  getTemporaryLocale,
  hasTranslation,
  localeMapping,
  resolveLocale,
  saveTemporaryLocale,
  translate,
  translateCountry,
} from "./utils";

export const SUPPORTED_LANGUAGES = [
  { value: "en", label: "English", flag: "🇺🇸" },
  { value: "es", label: "Español", flag: "🇪🇸" },
];

const I18nContext = createContext();

export const I18nProvider = ({ children, forceFallback = false }) => {
  const { globalState } = useGlobalContext();
  const isAuthenticated = globalState?.user?.authenticated;
  const userId = isAuthenticated ? globalState?.user?.id : null;

  const [locale, setLocale] = useState(() => getInitialLocale(forceFallback));
  const [localeLoading, setLocaleLoading] = useState({ userId: null, ready: false });
  const {
    preferences,
    updatePreferences,
    isSuccess: hasUserPreferences,
    isError,
    refetch,
  } = useUserPreferences({
    enabled: !forceFallback && Boolean(userId),
  });
  const pendingLocaleRef = useRef(null);
  const activeUserRef = useRef(userId);
  activeUserRef.current = forceFallback ? null : userId;
  const isLocaleReady = forceFallback || !userId || (localeLoading.userId === userId && localeLoading.ready);

  // Enhanced setLocale that handles temporary storage for unauthenticated users
  const setLocaleWithStorage = newLocale => {
    setLocale(newLocale);
    i18next.changeLanguage(newLocale);

    if (!isAuthenticated || !hasUserPreferences) {
      // Save an explicit sign-in choice after authentication.
      saveTemporaryLocale(newLocale);
    }
  };

  useEffect(() => {
    if (forceFallback) {
      setLocale(fallbackLocale);
      i18next.changeLanguage(fallbackLocale);
      return;
    }

    if (!userId) {
      const deviceLocale = resolveLocale(navigator.language);
      setLocale(getTemporaryLocale() || deviceLocale);
      i18next.changeLanguage(getTemporaryLocale() || deviceLocale);
      setLocaleLoading({ userId: null, ready: false });
      return;
    }

    if (!hasUserPreferences && !isError) {
      setLocaleLoading({ userId, ready: false });
      return;
    }

    const tempLocale = getTemporaryLocale();
    const nextLocale = resolveLocale(navigator.language, preferences?.[Preferences.locale], tempLocale);
    setLocale(nextLocale);
    i18next.changeLanguage(nextLocale);
    setLocaleLoading({ userId, ready: true });

    if (tempLocale && pendingLocaleRef.current !== userId) {
      pendingLocaleRef.current = userId;
      updatePreferences([{ id: Preferences.locale, value: nextLocale }], {
        onSuccess: () => {
          if (activeUserRef.current !== userId) return;
          if (getTemporaryLocale() === nextLocale) clearTemporaryLocale();
          UnityBridge.dispatchWebEvent({
            type: UnityBridge.OutgoingWebEvents.SetPreferences,
            payload: { [Preferences.locale]: nextLocale },
          });
        },
        onSettled: () => {
          if (pendingLocaleRef.current === userId) pendingLocaleRef.current = null;
        },
      });
    }
  }, [forceFallback, userId, preferences, hasUserPreferences, isError, updatePreferences]);

  useEffect(() => {
    if (forceFallback || !userId) return;

    const getUserPreferences = userInfo => {
      if (userInfo?.id && userInfo.id !== userId) return;
      refetch();
    };

    Events.auth.accountInfoUpdated.uniq(getUserPreferences);
    return () => Events.auth.accountInfoUpdated.remove(getUserPreferences);
  }, [forceFallback, userId, refetch]);

  useEffect(() => {
    const resetLocale = () => {
      activeUserRef.current = null;
      clearTemporaryLocale();
      const deviceLocale = forceFallback ? fallbackLocale : resolveLocale(navigator.language);
      setLocale(deviceLocale);
      i18next.changeLanguage(deviceLocale);
    };

    Events.auth.logout.uniq(resetLocale);

    return () => Events.auth.logout.remove(resetLocale);
  }, [forceFallback]);

  useEffect(() => {
    dayjs.locale(locale);
  }, [locale]);

  const i18n = (key, values = {}) => translate(localeMapping, locale, key, values);

  /**
   * Enhanced i18n function that supports React component placeholders
   * @param {string} key - The translation key
   * @param {Object} components - Object with component placeholders as keys and React components as values
   * @param {Object} values - Object with value placeholders as keys and string values
   * @returns {Array} Array of React nodes (strings and components)
   */
  const i18nWithComponents = (key, components = {}, values = {}) => {
    const translation = i18n(key, values);

    // Then handle component placeholders
    if (Object.keys(components).length === 0) {
      return translation; // No components to replace, return the string
    }

    // Split by component placeholders and map to array of strings and components
    const parts = translation.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, index) => {
      const match = part.match(/\{\{([^}]+)\}\}/);
      if (match && components[match[1]]) {
        return <Fragment key={index}>{components[match[1]]}</Fragment>;
      }
      return part ? <Fragment key={index}>{part}</Fragment> : null;
    });
  };

  const i18nExists = key => hasTranslation(localeMapping, locale, key);
  const countryNames = useMemo(() => createCountryNames(locale), [locale]);
  const tCountry = countryCode => translateCountry(countryNames, countryCode);

  const value = {
    i18n,
    i18nWithComponents,
    i18nExists,
    locale: forceFallback ? fallbackLocale : locale,
    isLocaleReady,
    tCountry,
    setLocale: setLocaleWithStorage,
    keys: Object.keys(localeMapping[fallbackLocale]),
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
};

export default useI18n;
